import { WIDGET_CSS } from './styles';

const TAG_NAME = 'bytax-feedback-widget';

export interface WidgetHost {
  element: HTMLElement;
  root: ShadowRoot;
  destroy(): void;
}

export function createHost(zIndex: number): WidgetHost {
  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, class BytaxFeedbackWidget extends HTMLElement {});
  }

  const element = document.createElement(TAG_NAME);
  // Children position themselves with position:fixed; the host box itself must
  // never affect page layout or intercept clicks.
  element.style.position = 'fixed';
  element.style.inset = 'auto';
  element.style.zIndex = String(zIndex);

  // Every event the widget raises is retargeted to this host element once it
  // crosses the shadow boundary — and this element is, by construction, outside
  // whatever layer the host page has open. Document-level handlers that treat
  // "outside" as a signal therefore misread widget interaction as interaction
  // with the page. Both halves of that are stopped here, in the bubble phase, so
  // the widget's own listeners (which sit below this element) have already run.
  //
  // Focus: host pages that open a modal (Radix/shadcn, Headless UI, MUI …) trap
  // focus with document-level focusin/focusout handlers and pull focus straight
  // back whenever it lands outside their modal.
  // This covers the focusin half only: when focus leaves an element *inside*
  // the host's modal, their focusout fires on their own element and never
  // passes through here. That half is handled by ui/dialog.ts opening the
  // dialog modally (an inert page makes their refocus a no-op); this listener
  // is what keeps the pre-<dialog> fallback path in that file usable.
  //
  // Pointer: the same libraries dismiss dialogs, popovers, drawers and menus
  // from a document-level pointer handler whenever the pointer goes down
  // outside the layer (Radix listens for `pointerdown`, Headless UI adds
  // `mousedown`, MUI's ClickAwayListener uses `click`/`touchend`). Without this,
  // merely clicking the launcher tears down the UI the user opened the widget to
  // report on — and it stays torn down while they pick and compose, so the
  // screenshot ends up capturing a page the reported element no longer sits on.
  // The picker blocks these on the way *down* (capture phase on document) while
  // it is running; this covers the launcher click that starts it and every
  // interaction with the widget's own dialog afterwards.
  const CONTAINED_EVENTS = [
    'focusin',
    'focusout',
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'click',
    'touchstart',
    'touchend',
  ] as const;
  for (const type of CONTAINED_EVENTS) {
    element.addEventListener(type, (event) => event.stopPropagation());
  }

  const root = element.attachShadow({ mode: 'open' });
  applyStyles(root);

  // body can be null if we're mounting before the parser has reached it
  // (e.g. a non-deferred script in <head>); documentElement always exists.
  const parent = document.body ?? document.documentElement;
  parent.appendChild(element);

  return {
    element,
    root,
    destroy() {
      element.remove();
    },
  };
}

function applyStyles(root: ShadowRoot): void {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(WIDGET_CSS);
    root.adoptedStyleSheets = [sheet];
  } catch {
    // Older engines without constructable stylesheets.
    const style = document.createElement('style');
    style.textContent = WIDGET_CSS;
    root.appendChild(style);
  }
}
